'use client'

import { useSiteConfig } from '@/feature/config/hooks/useSiteConfig.client'
import { twMerge } from '@/lib/classname/twMerge'
import { normalize } from '@/lib/formatters/string.formatter'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Button } from '@/ui/buttons/button/Button.client'
import { Card } from '@/ui/cards/card/Card'
import { Drawer, DrawerAction, DrawerBody, DrawerFooter } from '@/ui/drawer/drawer/Drawer.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { BaseSkeleton } from '@/ui/skeleton/BaseSkeleton'
import { Title } from '@/ui/text/title/Title'
import { useToast } from '@/ui/toast/Toast'
import { useState } from 'react'
import {
  AVERY_TEMPLATES,
  AVERY_TEMPLATES_MAP,
  type AveryTemplateName,
} from '../../services/products-stickers.avery-templates'
import { getProductStickersSessionStorage } from '../../services/products-stickers.session-storage.client'

function TemplateImage({ templateName, count }: { templateName: string; count: number }) {
  const [isLoaded, setIsLoaded] = useState(false)
  const [hasError, setHasError] = useState(false)

  return (
    <div className="border border-gray-100 bg-gray-50 relative h-[140px] w-[102px] shrink-0 overflow-hidden">
      {!isLoaded && <BaseSkeleton className="absolute inset-0 size-full" />}
      <img
        src={
          hasError ? '/placeholder-image.svg' : `/media/image/products-stickers/${templateName}.svg`
        }
        role="presentation"
        onLoad={() => setIsLoaded(true)}
        onError={() => {
          console.warn(
            `[ProductsStickersDrawer] Missing template image: /media/image/products-stickers/${templateName}.svg`,
          )
          setHasError(true)
        }}
        className={twMerge(
          'size-full object-cover transition-opacity duration-300',
          !isLoaded ? 'opacity-0' : 'opacity-100',
        )}
      />
      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform text-6xl font-bold text-secondary">
        {count}
      </span>
    </div>
  )
}

type ProductsStickersDrawerProps = {
  references: string[]
  triggerVariant: 'button' | 'link'
  filenameDetails: string
  onOpen: () => void
  onDownloadSuccess: (stickersPerSheet: number) => void
}

export function ProductsStickersDrawer({
  references,
  triggerVariant,
  filenameDetails,
  ...otherProps
}: ProductsStickersDrawerProps) {
  const { t } = useTranslation(['generic', 'ProductsStickersDrawer'])
  const [loadingTemplate, setLoadingTemplate] = useState<AveryTemplateName | null>(null)
  const toast = useToast()

  const { onOpen, onDownloadSuccess } = otherProps

  const { config } = useSiteConfig()
  const isStoreNameDisplayedOnProductLabel =
    config?.catalog?.isStoreNameDisplayedOnProductLabel ?? true
  const isProductLabelGenerationEnabled = config?.catalog?.isProductLabelGenerationEnabled

  if (!isProductLabelGenerationEnabled) {
    return null
  }

  const handleDownload = async (templateName: AveryTemplateName, stickersPerSheet: number) => {
    setLoadingTemplate(templateName)

    const brand = process.env.NEXT_PUBLIC_BRAND

    if (!brand) {
      setLoadingTemplate(null)
      throw new Error('NEXT_PUBLIC_BRAND environment variable is not defined')
    }

    const stickersText = t('ProductsStickersDrawer:filenames.stickers')
    const parts = [brand, stickersText, filenameDetails]

    const formatSuffix = templateName.split('-')[1] // Extract "1x7" from "L4760-1x7"
    const baseFilename = parts.filter(Boolean).join('-')
    const finalFilename = normalize(`${baseFilename}-${formatSuffix}`)
      .replace(/[^a-z0-9]+/g, '-') // Replace spaces and special chars with dashes
      .replace(/(^-+)|(-+$)/g, '') // Trim dashes

    const withCutLines = getProductStickersSessionStorage().data.withCutLines ?? false

    try {
      const response = await fetch('/api/products-stickers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          references,
          template: templateName,
          filename: finalFilename,
          siteOrigin: window.location.origin,
          translations: {
            referenceText: isStoreNameDisplayedOnProductLabel
              ? t('ProductsStickersDrawer:referenceText', {
                  companyName: t('generic:company'),
                  reference: '{{reference}}',
                })
              : t('ProductsStickersDrawer:referenceTextNoCompany', {
                  reference: '{{reference}}',
                }),
          },
          ...(withCutLines ? { withCutLines } : {}),
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))

        if (errorData.error === 'MISSING_PRODUCTS') {
          toast.error({
            title: t('ProductsStickersDrawer:toasts.error.missingProducts.title'),
            message: t('ProductsStickersDrawer:toasts.error.missingProducts.message', {
              references: errorData.missingReferences,
            }),
          })

          return
        }

        throw new Error('Failed to generate PDF')
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${finalFilename}.pdf`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      toast.success({
        message: t('ProductsStickersDrawer:toasts.success.title'),
      })

      onDownloadSuccess?.(stickersPerSheet)
    } catch (error) {
      console.error('Error generating PDF:', error)
      toast.error({
        title: t('ProductsStickersDrawer:toasts.error.generic.title'),
      })
    } finally {
      setLoadingTemplate(null)
    }
  }

  return (
    <Drawer
      size="sm"
      title={t('ProductsStickersDrawer:title')}
      trigger={
        triggerVariant === 'button' ? (
          <Button
            variant="primary"
            size="lg"
            iconLeft={<Icon.ScanBarcode />}
            onPress={() => onOpen()}
          >
            {t('ProductsStickersDrawer:triggerButtonLabel')}
          </Button>
        ) : (
          <Button
            size="md"
            variant="link"
            aria-label={t('ProductsStickersDrawer:triggerTitle')}
            iconLeft={<Icon.ScanBarcode />}
            className="flex justify-start"
            onPress={() => onOpen()}
          >
            {t('ProductsStickersDrawer:triggerTitle')}
          </Button>
        )
      }
    >
      <DrawerBody>
        <div className="flex flex-col gap-4">
          {AVERY_TEMPLATES.map((templateName) => {
            const template = AVERY_TEMPLATES_MAP[templateName]
            const count = template.rows * template.cols

            return (
              <Card key={templateName} skin="body-alt" padding="lg">
                <div className="flex flex-1 flex-col gap-3">
                  <div className="flex gap-3">
                    <TemplateImage templateName={templateName} count={count} />
                    <div className="flex flex-col justify-center gap-1">
                      <Title size="3xl" className="font-bold">
                        {t(`ProductsStickersDrawer:body.templates.${templateName}.title`)}
                      </Title>
                      <p>{t(`ProductsStickersDrawer:body.templates.${templateName}.summary`)}</p>
                      <div className="font-bold">
                        <p>{t(`ProductsStickersDrawer:body.quantity`, { count })}</p>
                        <p>
                          {t(`ProductsStickersDrawer:body.size`, {
                            stickerWidth: template.displayDimensions.width,
                            stickerHeight: template.displayDimensions.height,
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    size="lg"
                    iconLeft={<Icon.Download />}
                    onPress={() => handleDownload(templateName, count)}
                    isLoading={templateName === loadingTemplate}
                    isDisabled={!!loadingTemplate && templateName !== loadingTemplate}
                  >
                    {t('ProductsStickersDrawer:actions.download.title')}
                  </Button>
                </div>
              </Card>
            )
          })}
        </div>
      </DrawerBody>
      <DrawerFooter>
        <DrawerAction type="cancel">{t('generic:actions.cancel')}</DrawerAction>
      </DrawerFooter>
    </Drawer>
  )
}
